<template>
  <div class="helpy-container d-flex flex-column mt-4">
    <div class="w-100 bg-white">
      <tag-input v-model="helpyFilter" input-class="mb-2" :placeholder="$i18n.t('helpy_filter')"></tag-input>
    </div>
    <div v-show="showBackButton" @click="showSearchResults = true" class="w-100">
      <div class="pull-right el-link">
        <span class="material-icons md-12 me-1">arrow_back</span>
        <span>{{ $i18n.t('back') }}</span>
      </div>
    </div>
    <helpy-search-results
      v-if="showSearchResults"
      :results="searchResults"
      :filter="helpyFilter"
      @category-select="onCategorySelect"
      @content-select="onContentSelect"
    />
    <div v-else class="helpy-scroll-container d-flex row flex-grow-1">
      <div class="helpy-sidebar col-12 col-md-4 col-lg-3">
        <el-tree
          class="filter-tree"
          :data="helpyData"
          :props="defaultProps"
          accordion
          :highlight-current="false"
          @node-click="handleNodeClick"
          ref="tree"
        >
          <span class="d-flex align-items-center" slot-scope="{ node }" :id="`node--${node.id}`">
            <span class="material-icons md-18 me-1" :class="{ transparent: !node.data.icon }">
              {{ node.data.icon || 'square' }}
            </span>
            <span>{{ $i18n.t(node.label) }}</span>
          </span>
        </el-tree>
      </div>
      <div v-if="selectedNode" class="helpy-content col">
        <div class="align-items-center d-flex justify-content-between sticky-top bg-white pt-2">
          <div class="d-flex flex-column">
            <el-breadcrumb separator="/">
              <el-breadcrumb-item v-for="({ title, id }, index) in breadcrumb" :key="`content-bc-${id}`">
                <span
                  @click.prevent="onCategorySelect(id)"
                  :class="{
                    'el-link fw-bold color-primary': index !== breadcrumb.length - 1,
                  }"
                  >{{ $i18n.t(title) }}</span
                >
              </el-breadcrumb-item>
            </el-breadcrumb>
            <h1 class="helpy-title mt-3">{{ $i18n.t(selectedNode.title) }}</h1>
          </div>
          <el-button v-if="isUpdateAvailable" type="primary" @click="openUpdateForm(selectedLeaf)"
            >{{ $i18n.t('update') }}
          </el-button>
        </div>
        <template>
          <div v-if="listOfContents.length" v-html="firstLeavesContent" class="mb-3" />
          <ul v-if="listOfContents.length">
            <li v-for="{ title, id, icon } of listOfContents" :key="`content-table-${id}`">
              <el-link class="fw-bolder color-primary" @click="onCategorySelect(id)" :underline="false">
                <div class="d-flex align-items-center">
                  <span class="material-icons md-18 me-1" :class="{ transparent: icon === 'square' }">{{ icon }}</span>
                  <span>{{ $i18n.t(title) }}</span>
                </div>
              </el-link>
            </li>
          </ul>
          <el-tabs v-else id="helpy--tabs" @tab-click="onLeafSelect" v-model="selectedTab">
            <el-tab-pane v-for="(content, key) in selectedLeaves" :label="content.title" :key="`content-tab-${key}`">
              <div id="helpy--content" v-html="highlightedContent" />
            </el-tab-pane>
          </el-tabs>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import TagInput from './TagInput'
import HelpySearchResults from './HelpySearchResults'
import { difference, forEach, get, isEmpty, isEqual, map, pick, some, uniqWith } from 'lodash'
import { helpyTypes } from '../helpy-designer/helpyTypes'
import { htmlToText } from '../../utils/html-to-text'
import { highlight } from '../../utils'
import { mapActions } from 'vuex'
import EventBus from '../../utils/event-bus'

/**
 * @class Helpy
 *
 * @description This is the Helpy module viewer
 */
export default {
  name: 'helpy',
  components: {
    HelpySearchResults,
    TagInput,
  },
  data() {
    return {
      defaultProps: {
        children: 'nodes',
        label: 'title',
      },
      selectedTab: '0',
      isAdmin: false,
      updateUrl: '/helpy-designer/update',
      url: this.$CORE.requestController,
      leaves: [],
      searchResults: {
        categories: {},
        contents: [],
        globalResults: [],
      },
      showSearchResults: true,
    }
  },
  computed: {
    showBackButton() {
      return (
        !this.showSearchResults && (!isEmpty(this.searchResults.categories) || this.searchResults.contents.length > 0)
      )
    },
    helpyData: {
      get() {
        return this.$get('helpyData')
      },
      set(helpyData) {
        this.$dispatch('setHelpyData', { helpyData })
      },
    },
    helpyFilter: {
      get() {
        return this.$get('helpyFilter')
      },
      set(filter) {
        this.$dispatch('setHelpyFilter', { filter })
      },
    },
    index() {
      return this.$get('index')
    },
    selectedContent() {
      return get(this.selectedLeaves, [this.selectedTab, 'content'], '')
    },
    selectedNode: {
      get() {
        return this.$get('selectedNode')
      },
      set(selectedNode) {
        this.$dispatch('setSelectedNode', { selectedNode })
      },
    },
    selectedNodeId() {
      return get(this.selectedNode, 'id', null)
    },
    selectedLeaf: {
      get() {
        return this.$get('selectedLeaf')
      },
      set(selectedLeaf) {
        this.$dispatch('setSelectedLeaf', { selectedLeaf })
      },
    },
    showHelpy() {
      return this.$get('showHelpy')
    },
    selectedLeaves() {
      return get(this.selectedNode, 'leaves', [])
    },
    breadcrumb() {
      return this.getBreadcrumb(this.selectedNodeId)
    },
    isUpdateAvailable() {
      return this.isAdmin && this.selectedLeaf
    },
    firstLeavesContent() {
      return get(this.selectedNode, 'leaves.0.content')
    },
    listOfContents() {
      return map(get(this.selectedNode, 'nodes', []), (node) => {
        return { title: node.title, id: node.id, icon: node.icon || 'square' }
      })
    },
    highlightedContent() {
      let content = `${this.selectedContent}`
      this.helpyFilter.forEach((tag) => {
        const regex = new RegExp(`${tag}`, 'ig') // @regex case-insensitive regex for tag
        if (content.match(regex)) {
          const highlight = this.highlight(content, tag, 'helpy-highlight')
          if (highlight.success) {
            content = highlight.html
          }
        }
      })
      return content
    },
  },
  methods: {
    ...mapActions('popup', ['setOpened', 'setUrl', 'setType']),
    openUpdateForm(id) {
      const url = `/helpy-designer/update?mongoId=${id}`
      this.setUrl(url)
      this.setType('HelpyForm')
      this.setOpened()
    },
    async load() {
      this.helpyData = (
        await this.$client.execute({
          operationId: 'HelpyCategory_getHelpyCategories',
          parameters: {
            url: null,
            type: helpyTypes[0].value,
          },
        })
      ).body
      this.helpyData = this.helpyData.filter((node) => this.hasDefault(node))
      this.buildIndex({ title: 'root', id: 'root', path: [] }, this.helpyData)
      this.buildLeaves(this.helpyData, true)
    },
    checkPermission() {
      this.$client
        .execute({
          operationId: 'Authorities_getPermissionByUrl',
          parameters: { url: this.updateUrl },
        })
        .then((res) => {
          this.isAdmin = !!get(res, ['obj', this.updateUrl, 2])
        })
    },
    handleNodeClick(node) {
      this.selectedTab = '0'
      this.selectedNode = node
      this.selectFirstLeaf()
    },
    buildIndex(parent, nodes) {
      for (const nodesKey in nodes) {
        const title = nodes[nodesKey].title
        const id = nodes[nodesKey].id
        const icon = nodes[nodesKey].icon || 'square'
        const node = {
          title,
          icon,
          id,
          path: parent.path.concat(parent.path.length ? ['nodes', +nodesKey] : [+nodesKey]),
          parent: parent.id,
        }
        this.$dispatch('setIndex', { id, node })
        this.buildIndex(node, nodes[nodesKey].nodes)
      }
    },
    findCategoriesByTitle() {
      let matches = []
      const regex = new RegExp(this.url.replace(/([[()#$^*])/g, '\\$1'), 'ig') // @regex case-insensitive regexp for search tag
      forEach(this.index, (item) => {
        if (item.title.match(regex)) matches.push(item.id)
      })
      return pick(this.index, matches)
    },
    findContentByUrlTitleContent() {
      const urlRegexp = new RegExp(this.url.replace(/([[()#$^*])/g, '\\$1'), 'ig') // @regex case-insensitive regexp for search tag
      const filteredByUrl = this.leaves.filter((content) => content?.url?.find((url) => url.match(urlRegexp)))
      if (!this.helpyFilter.length) return filteredByUrl

      return filteredByUrl.filter((content) => {
        let match = true
        this.helpyFilter.forEach((tag) => {
          const regex = new RegExp(tag.replace(/([[()#$^*])/g, '\\$1'), 'ig') // @regex case-insensitive regexp for search tag
          if (content.title.match(regex) || htmlToText(content.content).match(regex)) {
            match = false
          }
        })
        return match
      })
    },
    findMatchingGlobalContent() {
      if (!this.helpyFilter) return []
      return this.leaves.filter((content) => {
        let match = false
        this.helpyFilter.forEach((tag) => {
          const regex = new RegExp(tag.replace(/([[()#$^*])/g, '\\$1'), 'ig') // @regex case-insensitive regexp for search tag
          if (content.title.match(regex) || htmlToText(content.content).match(regex)) {
            match = true
          }
        })
        return match
      })
    },
    getBreadcrumb(nodeId) {
      if (!nodeId) return []
      const parentId = get(this.index, [nodeId, 'parent'], 'root')
      const parentNode = get(this.index, parentId, 'root')
      const parentTitle = get(parentNode, 'title', 'root')
      return parentTitle === 'root' ? [this.index[nodeId]] : this.getBreadcrumb(parentId).concat([this.index[nodeId]])
    },
    buildLeaves(nodes, reset = false) {
      if (reset) {
        this.leaves = []
      }
      nodes.forEach((node) => {
        if (node?.leaves) {
          node.leaves.forEach((leaf, index) => {
            leaf.tabIndex = index
            leaf.parent = node.id
          })
          this.leaves.push(...node.leaves)
        } else if (node?.nodes) {
          this.buildLeaves(node.nodes)
        }
      })
    },
    getNodeById(id) {
      const node = get(this.index, id, {})
      const path = get(node, 'path', [])
      return get(this.helpyData, path)
    },
    highlight,
    onLeafSelect({ index }) {
      this.selectedTab = index.toString()
      this.selectedLeaf = get(this.selectedNode, ['leaves', index, 'id'], null)
    },
    onCategorySelect(id) {
      this.showSearchResults = false
      this.selectedNode = this.getNodeById(id)
      this.selectFirstLeaf()
    },
    onContentSelect({ parent, index }) {
      this.selectedNode = this.getNodeById(parent)
      this.onLeafSelect({ index })
      this.showSearchResults = false
    },
    hasDefault(node) {
      if (node.hasOwnProperty('leaves')) {
        return true
      } else if (node.hasOwnProperty('nodes')) {
        return some(node.nodes.map((node) => this.hasDefault(node)))
      } else {
        return false
      }
    },
    searchHelpys() {
      this.searchResults.categories = this.findCategoriesByTitle()
      this.searchResults.contents = uniqWith(this.findContentByUrlTitleContent(), isEqual)
      this.searchResults.globalResults = difference(this.findMatchingGlobalContent(), this.searchResults.contents)
      this.showSearchResults = true
    },
    selectFirstLeaf() {
      if (this.selectedNode.hasOwnProperty('leaves')) {
        this.onLeafSelect({ index: 0 })
      } else {
        this.selectedLeaf = null
      }
    },
  },
  async mounted() {
    this.checkPermission()
    await this.load()
    EventBus.$on('refresh-helpy-tree', async () => {
      await this.load()
    })
    this.searchHelpys()
  },
  beforeDestroy() {
    EventBus.$off('refresh-helpy-tree')
  },
  watch: {
    helpyFilter: {
      immediate: true,
      handler() {
        this.searchHelpys()
      },
    },
    showHelpy(value) {
      if (value) this.searchHelpys()
    },
  },
}
</script>

<style lang="scss" scoped>
.color-primary {
  color: $color-primary;
}
.helpy-sidebar {
  border-bottom: 1px solid $border-color;
  height: 25%;
  overflow: scroll;
}
.helpy-content {
  padding: 0 10px 0 40px;
  height: 75%;
  overflow: scroll;
  div,
  p,
  em {
    line-height: 1.5rem;
  }
}
.helpy-container {
  height: calc(100vh - 80px);
  padding: 0 40px 0 20px;
}
.helpy-scroll-container {
  overflow: scroll;
}
.helpy--info {
  font-size: 12px;
  color: $color-text-regular;
}
.profile-picture {
  height: 35px;
  width: 35px;
  object-fit: cover;
  border-radius: 50%;
}
@media screen and (min-width: 768px) {
  .helpy-sidebar {
    height: 100%;
    max-width: 350px;
    border-bottom: none;
    border-right: 1px solid $border-color;
  }
  .helpy-content {
    height: 100%;
    .sticky-top {
      outline: 1px solid $white;
    }
  }
}
</style>

<style lang="scss">
.helpy-highlight {
  background-color: $info-background-color;
}
.helpy-content pre {
  display: inline-block;
  background-color: $border-color;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.8rem;
  max-width: 95%;
  vertical-align: middle;
}
#helpy--tabs div.el-tabs__header.is-top {
  position: sticky;
  z-index: 1;
  background-color: $white;
  top: 80px;
}
</style>
